import { Button, Col, Container, Form, Image, Row, Spinner } from 'react-bootstrap'
import DashboardLayout from '../../../layouts/Dashboard'
import { useLayoutEffect, useState } from 'react'
import { IMaskInput } from 'react-imask'
import api from '../../../services/api'
import UserCircle from '../../../assets/UserCircle.svg'
import { toast } from 'react-toastify'
import { states } from '../../../utils/states'
import { useNavigate } from 'react-router-dom'
import ChangePasswordModal from '../../../components/ChangePasswordModal'
import { Lock } from 'phosphor-react'
import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import { errorApi } from '../../../utils/errorApi'

const EmpresaEditar: React.FC = () => {
  const [id, setId] = useState<number>(0)

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)

  const { isLoading, setIsLoading } = useLoading()
  const [profilePicture, setProfilePicture] = useState<File | null>()

  const [profilePictureUrl, setProfilePictureUrl] = useState<string>('')

  const [brandName, setBrandName] = useState('')
  const [phone, setPhone] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [state, setState] = useState<string>('')

  const navigate = useNavigate()

  useLayoutEffect(() => {
    async function loadData() {
      setIsLoading(true)

      try {
        const { data } = await api.get('/company/companies')

        setId(data.user_id)
        setBrandName(data.brand_name)
        setPhone(data.phone)
        setCity(data.city)
        setState(data.state)
        if (data.profile_picture) setProfilePictureUrl(data.profile_picture.url)
        else setProfilePictureUrl(UserCircle)
      } catch (error) {
        toast.error(
          errorApi(error, 'Falha ao buscar os dados da empresa. Tente novamente mais tarde.'),
        )
      }
      setIsLoading(false)
    }
    loadData()
  }, [setIsLoading])

  const clearFileInput = (e: any) => {
    const filesData = new DataTransfer()
    e.target.files = filesData.files
  }

  const handlePictureChange = (e: any) => {
    if (e.target.files) {
      const file = e.target.files[0]
      const fileType = file.name.split('.')[1].toLowerCase()

      if (!['jpg', 'png', 'gif', 'jpeg'].includes(fileType)) {
        toast.error('A foto deve possuir formato .PNG, .JPG, .JPEG ou .GIF')
        clearFileInput(e)
        setProfilePicture(null)
        setProfilePictureUrl(UserCircle)
      } else {
        setProfilePicture(e.target.files[0])
        const url = URL.createObjectURL(e.target.files[0])
        setProfilePictureUrl(url)
      }
    }
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    setIsLoading(true)

    if (!phone || !city || !state || !brandName) {
      toast.error('Você precisa preencher todos os campos obrigatórios!')
      setIsLoading(false)
      return
    }

    const data = new FormData()
    const model = {
      brand_name: brandName,
      phone: phone,
      city: city,
      state: state,
    }

    data.append('data', JSON.stringify(model))
    if (profilePicture) data.append('image', profilePicture, profilePicture.name)

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }

    try {
      await api.put(`/company/companies/${id}`, data, config)

      toast.info('Empresa atualizada com sucesso!', {
        autoClose: 3000,
      })
      navigate('/empresa')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao atualizar a empresa! Verifique os dados.'))
    }
    setIsLoading(false)
  }

  const isValidModel = () => {
    return brandName && phone && city && state
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Editar empresa">
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      <Container>
        <h2 style={{ textAlign: 'center' }}>Atualizar dados da empresa</h2>
        <Col style={{ textAlign: 'center' }}>
          <Image className={styles.profile_picture} fluid={true} src={profilePictureUrl} />
        </Col>
        <Col className={styles.form_container}>
          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Nome*</Form.Label>
              <Form.Control
                className={styles.form_control}
                placeholder="Digite aqui o nome"
                value={brandName}
                onChange={(event) => setBrandName(event.target.value)}
              />
            </Col>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Foto de perfil (opcional)</Form.Label>
              <Form.Control
                className={styles.form_control}
                type="file"
                placeholder="Selecione a foto de perfil"
                onChange={(event) => {
                  handlePictureChange(event)
                }}
              />
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Telefone*</Form.Label>
              <Form.Control
                className={styles.form_control}
                as={IMaskInput}
                mask={[
                  { mask: '(00) 00000000' }, // para números fixos (8 dígitos)
                  { mask: '(00) 000000000' }, // para números de celular (9 dígitos)
                ]}
                placeholder="Digite aqui o telefone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </Col>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Cidade*</Form.Label>
              <Form.Control
                className={styles.form_control}
                placeholder="Digite aqui a cidade"
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col md={6} className={styles.form_col}>
              <Form.Label className={styles.form_label}>Estado/UF*</Form.Label>
              <Form.Select
                className={styles.form_control}
                value={state}
                onChange={(event) => setState(event.target.value)}
              >
                <option value="">Selecione o estado</option>
                {states.map((state) => (
                  <option key={state}>{state}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row className={styles.form_row}>
            <Col
              className={`d-flex justify-content-md-start justify-content-center ${styles.form_col}`}
            >
              <Button
                className={styles.change_password_button}
                onClick={() => setShowChangePasswordModal(true)}
              >
                Alterar senha <Lock className={styles.lock_icon} size={16} weight={'bold'} />
              </Button>
            </Col>
          </Row>
          <Row>
            <Col
              style={{ textAlign: 'right' }}
              className="d-flex justify-content-md-end justify-content-center"
            >
              <Button
                className={styles.button}
                disabled={isLoading || !isValidModel()}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <Spinner animation="border" size="sm" className={styles.spinner_button} />
                ) : (
                  'Atualizar empresa'
                )}
              </Button>
            </Col>
          </Row>
        </Col>
      </Container>
      <ChangePasswordModal
        closeModal={() => setShowChangePasswordModal(false)}
        show={showChangePasswordModal}
        id={id}
      ></ChangePasswordModal>
    </DashboardLayout>
  )
}

export default EmpresaEditar
